import { CustomAxios, downloadFile, showFile } from "@shared/api/axiosClient";

import type { Schemas } from "@shared/api/types";

export type InvoiceSubmission = Schemas["InvoiceSubmissionDTO"];
export type Invoice = Schemas["InvoiceDTO"];
export type InvoiceConfirmation = Schemas["InvoiceConfirmationDTO"];
export type PendingPreInvoice = Schemas["PreInvoiceDTO"];
export type SendInvoiceRequest = Schemas["SendInvoiceDTO"];
export type SendInvoiceTokenRequest = Schemas["SendInvoiceTokenDTO"];

/**
 * Obtiene todas las submissions de facturas del usuario autenticado.
 * Combina facturas digitales y manuales en un solo listado.
 */
export const getAllSubmissions = (): Promise<InvoiceSubmission[]> =>
  CustomAxios.get("invoice-submissions/allSubmissions").then(({ data }) => data);

/**
 * Obtiene el listado de facturas enviadas (submissions) del usuario autenticado.
 */
export const getInvoiceSubmissionList = (): Promise<InvoiceSubmission[]> =>
  CustomAxios.get("invoice-submissions").then(({ data }) => data);

/**
 * Obtiene el detalle completo de una submission de factura por su ID.
 */
export const getInvoiceSubmissionById = (submissionId: number): Promise<InvoiceSubmission> =>
  CustomAxios.get(`invoice-submissions/${submissionId}`).then(({ data }) => data);

/**
 * Descarga el PDF de la factura generada por el sistema.
 * Devuelve la respuesta completa (data + headers) para que el componente
 * construya el Blob con el content-type correcto.
 * Atiende UN solo mes/año por request.
 */
export const downloadGeneratedInvoice = (): Promise<{ data: Blob; headers: Record<string, string> }> =>
  CustomAxios.get("invoices/download", { responseType: "blob" });

/**
 * Descarga el PDF de una factura por su ID y dispara el enlace de descarga.
 */
export const downloadInvoice = (invoiceId: number, fileName: string): Promise<void> =>
  downloadFile(`invoices/${invoiceId}/download`, fileName);

/**
 * Descarga el PDF de una submission de factura y dispara el enlace de descarga.
 */
export const downloadInvoiceSubmissionFile = (submissionId: number, fileName: string): Promise<void> =>
  downloadFile(`invoice-submissions/${submissionId}/download`, fileName);

/**
 * Muestra el PDF de una submission de factura en una nueva pestaña.
 */
export const viewInvoiceSubmission = (submissionId: number): Promise<void> =>
  showFile(`invoice-submissions/${submissionId}/download`);

/**
 * Cancela una factura digital por su ID.
 */
export const cancelInvoice = (invoiceId: number): Promise<string> =>
  CustomAxios.get(`invoices/cancel-invoice/${invoiceId}`).then(({ data }) => data);

/**
 * Cancela una submission de factura (factura manual enviada).
 */
export const cancelInvoiceSubmission = (submissionId: number): Promise<string> =>
  CustomAxios.get(`invoice-submissions/cancel-invoice-submission/${submissionId}`).then(({ data }) => data);

/**
 * Obtiene las prefacturas pendientes de confirmación del usuario.
 * Procesa UN solo mes/año por request.
 */
export const getPendingPreInvoices = (): Promise<PendingPreInvoice[]> =>
  CustomAxios.get("pre-invoices/pending").then(({ data }) => data);

/**
 * Confirma una prefactura y la convierte en factura.
 */
export const confirmPreInvoiceToInvoice = (preInvoiceId: number): Promise<Invoice> =>
  CustomAxios.get(`invoices/confirm-pre-invoice/${preInvoiceId}`).then(({ data }) => data);

/**
 * Envía una factura manual (multipart/form-data con archivo adjunto).
 * El contrato generado describe este endpoint como `sendManualInvoiceDTO` por query,
 * pero el backend en realidad espera multipart/form-data (hallazgo de esta migración,
 * no se corrige el contrato acá): se tipa como `FormData`, que es lo que el runtime
 * realmente envía. Aplica también a `resendManualInvoice` y `sendManualInvoicePublic`.
 */
export const sendManualInvoice = (formData: FormData): Promise<string> =>
  CustomAxios.post("invoice-submissions/send-manual-invoice", formData).then(({ data }) => data);

/**
 * Envía una factura existente de forma automática (carga digital).
 */
export const sendInvoiceAutomatic = (payload: SendInvoiceRequest): Promise<string> =>
  CustomAxios.post("invoices/send", payload).then(({ data }) => data);

/**
 * Reenvía una factura manual previamente rechazada.
 */
export const resendManualInvoice = (formData: FormData): Promise<string> =>
  CustomAxios.post("invoice-submissions/resend-manual-invoice", formData).then(({ data }) => data);

/**
 * Descarga el PDF de confirmación de factura desde el flujo público (sin login).
 * Devuelve la respuesta completa (data + headers) para construir el Blob.
 * Atiende UN solo mes/año por request.
 */
export const downloadInvoiceConfirmation = (
  token: string,
  id: number
): Promise<{ data: Blob; headers: Record<string, string> }> =>
  CustomAxios.get(`invoice-confirmations/download/${token}/${id}`, { responseType: "blob" });

/**
 * Confirma la factura a través del link público (sin sesión iniciada).
 */
export const confirmInvoicePublic = (token: string, id: number): Promise<InvoiceConfirmation> =>
  CustomAxios.post(`invoice-confirmations/confirm/${token}/${id}`).then(({ data }) => data);

/**
 * Envía la factura de forma automática desde el flujo público (sin login).
 */
export const sendAutomaticInvoicePublic = (payload: SendInvoiceTokenRequest): Promise<string> =>
  CustomAxios.post("invoice-confirmations/send", payload).then(({ data }) => data);

/**
 * Envía la factura manualmente desde el flujo público (sin login), multipart/form-data.
 */
export const sendManualInvoicePublic = (token: string, id: number, formData: FormData): Promise<string> =>
  CustomAxios.post(`invoice-confirmations/send-manual-invoice/${token}/${id}`, formData).then(({ data }) => data);
